import { useState, type ReactNode } from 'react'

import {
  countGateStop,
  type TransportOptions,
  type TransportService,
} from '../../api/transport'
import {
  EMPTY_BOOKING,
  useBooking,
  type Booking,
  type CarriedTrip,
} from './booking'
import { resolveCarried } from './carry'
import { SERVICE_NAME } from './services'
import { BookingProvider } from './BookingState'
import { PassengerScreen } from './PassengerScreen'
import { ReviewScreen } from './ReviewScreen'
import { ServiceScreen, type ServiceNotice } from './ServiceScreen'
import { serviceCard, type Card } from './services'
import { StartScreen } from './StartScreen'
import { TripScreen } from './TripScreen'

/**
 * The booking flow (M39, spec §C.3), shown once the options answer, i.e. once
 * booking is switched on. Its state lives in this tree and dies with it: a
 * reload, or the gate's stop, starts again from Screen 1 (M39-Q3).
 */
export function TransportFlow({
  carried = null,
  ...props
}: FlowProps & {
  /** "Book the same trip again" from the booking page (M40-T11), taken
   * from memory by `Transport` as /transport opened. */
  carried?: CarriedTrip | null
}): ReactNode {
  const [initial] = useState<Partial<Booking> | undefined>(() =>
    carried ? { carried: resolveCarried(carried, props.options) } : undefined,
  )
  return (
    <BookingProvider {...(initial ? { initial } : {})}>
      <FlowSteps {...props} />
    </BookingProvider>
  )
}

type FlowProps = {
  options: TransportOptions
  /** Focus Screen 1's heading on arrival (a restart after a stop, or a trip
   * carried in from another page). */
  focusHeading: boolean
  onStop: () => void
}

type Step = 'start' | 'service' | 'trip' | 'details' | 'review'

/** Exported for tests, which read the booking state beside it. */
export function FlowSteps({
  options,
  focusHeading,
  onStop,
  startAt = 'start',
}: FlowProps & {
  /** For page tests that start part-way through, with `BookingProvider`'s
   * `initial` answers. */
  startAt?: Step
}): ReactNode {
  const { booking, update } = useBooking()
  const [step, setStep] = useState<Step>(startAt)
  const [returned, setReturned] = useState(focusHeading)
  // "Edit" on Screen 4's mobility summary (M39-T12): Screen 1, then back.
  const [editing, setEditing] = useState(false)
  // Why Screen 2 is shown again, when an edited answer ruled the service out.
  const [serviceNotice, setServiceNotice] = useState<ServiceNotice | null>(null)

  // Counted from the tap itself (M39-Q1, moved here from M39-T08), never on
  // mount, so a reload of the stop screen does not count again. Not waited
  // for, and a failure is silent.
  const stop = () => {
    countGateStop()
    onStop()
  }

  if (step === 'review') {
    return (
      <ReviewScreen
        options={options}
        onBack={() => setStep('details')}
        onChange={setStep}
        onStop={onStop}
        onBookAgain={(carried) => {
          // A new booking, from the safety check, with the trip carried. The
          // address bar holds the private link (T09): back to /transport.
          window.history.replaceState(window.history.state, '', '/transport')
          update({ ...EMPTY_BOOKING, carried })
          setEditing(false)
          setServiceNotice(null)
          setReturned(true)
          setStep('start')
        }}
      />
    )
  }
  if (step === 'details') {
    return (
      <PassengerScreen
        options={options}
        onBack={() => setStep('trip')}
        onEditMobility={() => {
          setEditing(true)
          setReturned(true)
          setStep('start')
        }}
        onStop={onStop}
        onContinue={() => setStep('review')}
      />
    )
  }
  if (step === 'trip') {
    return (
      <TripScreen
        options={options}
        onBack={() => setStep('service')}
        onStop={onStop}
        onContinue={() => setStep('details')}
      />
    )
  }
  if (step === 'service') {
    return (
      <ServiceScreen
        options={options}
        notice={serviceNotice}
        onBack={() => {
          setServiceNotice(null)
          setReturned(true)
          setStep('start')
        }}
        onStop={onStop}
        onContinue={() => {
          setServiceNotice(null)
          setStep('trip')
        }}
      />
    )
  }
  return (
    <StartScreen
      options={options}
      focusHeading={returned}
      onStop={stop}
      keepService={editing}
      {...(editing
        ? {
            onBack: () => {
              setEditing(false)
              setStep('details')
            },
          }
        : {})}
      onContinue={(answers) => {
        const cardFor = (service: TransportService) =>
          serviceCard(
            service,
            options.eligibility.find(
              (r) =>
                r.mobility === answers.mobility &&
                r.uses_oxygen === answers.usesOxygen,
            ),
            options,
          )
        if (!editing) {
          // "Book the same trip again" (M40-T11): the earlier service is
          // chosen on Screen 2 if the new answers still allow it; otherwise
          // Screen 2 says why (the M39-T12 re-check). Only while nothing is
          // chosen yet: a later visit to Screen 1 keeps the person's choice.
          const carried = booking.carried
          if (carried && !booking.service && !booking.trip) {
            const card = cardFor(carried.service)
            update({
              service: card.kind === 'open' ? carried.service : null,
              trip: null,
            })
            setServiceNotice(
              card.kind === 'open'
                ? null
                : {
                    lead: `${SERVICE_NAME[carried.service]}, as on your last trip, can't be booked for these answers.`,
                    reason: why(card),
                  },
            )
          }
          setStep('service')
          return
        }
        // The service chosen earlier, re-checked against the new answers
        // with the matrix (spec §C.3.4): still allowed, back to Screen 4 with
        // everything kept; otherwise Screen 2, saying why.
        setEditing(false)
        const card = booking.service ? cardFor(booking.service) : null
        if (card?.kind === 'open') {
          setStep('details')
          return
        }
        update({ service: null, trip: null })
        setServiceNotice(
          card
            ? {
                lead: 'Your answers changed, so please choose again.',
                reason: why(card),
              }
            : null,
        )
        setStep('service')
      }}
    />
  )
}

/** A refused card's reason, in words (as Screen 2's cards say it). */
function why(card: Card): string | null {
  switch (card.kind) {
    case 'reason':
      return card.reason
    case 'offline':
      return 'That service is not available online yet.'
    case 'members':
      return 'Members book that service by phone.'
    case 'open':
      return null
  }
}
